import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as Notifications from '../lib/notifications';
import Constants from 'expo-constants';
import * as Crypto from 'expo-crypto';
import * as SecureStore from 'expo-secure-store';
import type { Session } from '@supabase/supabase-js';
import { trpc } from '../lib/trpc';
import { supabase } from '../lib/supabase';
import {
  sendClientObservability,
  serializeClientError,
  type ObservabilityScalar,
} from '../lib/observability';

// Configure how notifications are presented when the app is in the foreground
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

const PUSH_TOKEN_STORAGE_KEY = 'push.expoToken';
const PUSH_INSTALLATION_ID_STORAGE_KEY = 'push.installationId';
const ANDROID_NOTIFICATION_CHANNEL_ID = 'default';
const EXPO_PUSH_TOKEN_PATTERN = /^(ExponentPushToken|ExpoPushToken)\[[^\]]+\]$/;

async function ensureAndroidNotificationChannel(): Promise<void> {
  if (Platform.OS !== 'android') return;

  await Notifications.setNotificationChannelAsync(ANDROID_NOTIFICATION_CHANNEL_ID, {
    name: 'Per Diem',
    importance: Notifications.AndroidImportance.DEFAULT,
  });
}

async function getStoredPushToken(): Promise<string | null> {
  if (Platform.OS === 'web') return null;
  return SecureStore.getItemAsync(PUSH_TOKEN_STORAGE_KEY);
}

async function setStoredPushToken(token: string): Promise<void> {
  if (Platform.OS === 'web') return;
  await SecureStore.setItemAsync(PUSH_TOKEN_STORAGE_KEY, token);
}

async function clearStoredPushToken(): Promise<void> {
  if (Platform.OS === 'web') return;
  await SecureStore.deleteItemAsync(PUSH_TOKEN_STORAGE_KEY);
}

async function getOrCreatePushInstallationId(): Promise<string | null> {
  if (Platform.OS === 'web') return null;

  const existing = await SecureStore.getItemAsync(PUSH_INSTALLATION_ID_STORAGE_KEY);
  if (existing) return existing;

  const installationId = Crypto.randomUUID();
  await SecureStore.setItemAsync(PUSH_INSTALLATION_ID_STORAGE_KEY, installationId);
  return installationId;
}

type PushEventScope = 'registration' | 'unregister';

function trackPushEvent(
  scope: PushEventScope,
  status: string,
  metadata: Record<string, ObservabilityScalar> = {},
): void {
  void sendClientObservability({
    kind: 'event',
    eventName: `push.${scope}.${status}`,
    component: 'usePushNotifications',
    metadata: {
      status,
      os: Platform.OS,
      appVersion: Constants.expoConfig?.version ?? '0.0.0',
      ...metadata,
    },
  });
}

function pushErrorMetadata(
  err: unknown,
  failureStage: string,
): Record<string, ObservabilityScalar> {
  const serialized = serializeClientError(err);
  return {
    failureStage,
    errorName: serialized.errorName ?? null,
    errorMessage: serialized.errorMessage ?? null,
    errorBucket: serialized.fingerprint ?? serialized.errorName ?? 'unknown',
  };
}

function isExpoPushToken(value: string): boolean {
  return EXPO_PUSH_TOKEN_PATTERN.test(value.trim());
}

async function getExpoPushToken(
  source: 'initial' | 'refresh',
  devicePushToken?: Notifications.DevicePushToken,
): Promise<string> {
  const projectId = Constants.expoConfig?.extra?.eas?.projectId;
  const tokenData = await Notifications.getExpoPushTokenAsync({
    ...(projectId ? { projectId } : {}),
    ...(devicePushToken ? { devicePushToken } : {}),
  });
  const expoToken = tokenData.data;

  if (!isExpoPushToken(expoToken)) {
    trackPushEvent('registration', 'token_fetch_failed', {
      source,
      hasProjectId: Boolean(projectId),
      failureStage: 'validate_expo_token',
      errorName: 'InvalidExpoPushToken',
      errorMessage: 'Expo returned a token with an unexpected format',
      errorBucket: 'invalid_expo_push_token_format',
    });
    throw new Error('Expo returned a token with an unexpected format');
  }

  trackPushEvent('registration', 'token_fetched', {
    source,
    hasProjectId: Boolean(projectId),
  });
  return expoToken;
}

/**
 * Request push notification permissions, get the Expo push token,
 * and register the device with the API.
 *
 * Call this hook once the user is authenticated (inside the (app) layout).
 */
export function usePushNotifications() {
  const registeredUserIdRef = useRef<string | null>(null);
  const isRegisteringRef = useRef(false);
  const failedRef = useRef(false);
  const [session, setSession] = useState<Session | null>(null);
  const registerDevice = trpc.notifications.registerDevice.useMutation();

  async function registerToken(
    expoToken: string,
    sessionUserId: string,
    source: 'initial' | 'refresh',
  ): Promise<void> {
    const previousExpoToken = await getStoredPushToken();
    const installationId = await getOrCreatePushInstallationId();
    const appVersion = Constants.expoConfig?.version ?? '0.0.0';
    const tokenChanged = Boolean(previousExpoToken && previousExpoToken !== expoToken);

    try {
      const result = await registerDevice.mutateAsync({
        expoToken,
        ...(installationId ? { installationId } : {}),
        ...(tokenChanged && previousExpoToken ? { previousExpoToken } : {}),
        os: Platform.OS,
        version: appVersion,
        isTesting: __DEV__,
      });
      trackPushEvent('registration', 'register_succeeded', {
        source,
        hasInstallationId: Boolean(installationId),
        hadPreviousToken: Boolean(previousExpoToken),
        tokenChanged,
        deviceId: result.deviceId ?? null,
      });
    } catch (err) {
      trackPushEvent('registration', 'register_failed', {
        source,
        hasInstallationId: Boolean(installationId),
        hadPreviousToken: Boolean(previousExpoToken),
        tokenChanged,
        ...pushErrorMetadata(err, 'register_device'),
      });
      throw err;
    }

    await setStoredPushToken(expoToken);

    registeredUserIdRef.current = sessionUserId;
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user.id ?? null;
      setSession(session);

      if (!userId) {
        registeredUserIdRef.current = null;
      }
      failedRef.current = false;
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Push notifications are not supported on web
    if (Platform.OS === 'web') return;
    if (!session?.user.id || !session.access_token) return;
    if (isRegisteringRef.current || failedRef.current) return;
    const sessionUserId = session.user.id;
    if (registeredUserIdRef.current === sessionUserId) return;

    async function register() {
      isRegisteringRef.current = true;
      try {
        trackPushEvent('registration', 'started', { source: 'initial' });
        await ensureAndroidNotificationChannel();

        // Check / request permissions
        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;

        if (existingStatus !== 'granted') {
          const { status } = await Notifications.requestPermissionsAsync();
          finalStatus = status;
        }

        if (finalStatus !== 'granted') {
          trackPushEvent('registration', 'permission_denied', {
            source: 'initial',
            permissionStatus: finalStatus,
          });
          if (__DEV__) {
            console.log('[push] Permission not granted');
          }
          return;
        }
        trackPushEvent('registration', 'permission_granted', {
          source: 'initial',
          permissionStatus: finalStatus,
        });

        // Get Expo push token
        let expoToken: string;
        try {
          expoToken = await getExpoPushToken('initial');
        } catch (err) {
          trackPushEvent('registration', 'token_fetch_failed', {
            source: 'initial',
            ...pushErrorMetadata(err, 'fetch_token'),
          });
          throw err;
        }

        if (__DEV__) {
          console.log('[push] Expo push token:', expoToken);
        }

        await registerToken(expoToken, sessionUserId, 'initial');
      } catch (err) {
        failedRef.current = true;
        trackPushEvent('registration', 'failed', {
          source: 'initial',
          ...pushErrorMetadata(err, 'register_flow'),
        });
        if (__DEV__) {
          console.warn('[push] Failed to register for push notifications:', {
            message: err instanceof Error ? err.message : String(err),
            data:
              typeof err === 'object' && err !== null && 'data' in err
                ? (err as { data?: unknown }).data
                : undefined,
            shape:
              typeof err === 'object' && err !== null && 'shape' in err
                ? (err as { shape?: unknown }).shape
                : undefined,
          });
        }
      } finally {
        isRegisteringRef.current = false;
      }
    }

    register();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!session?.user.id) return;

    const sessionUserId = session.user.id;
    const subscription = Notifications.addPushTokenListener((token) => {
      trackPushEvent('registration', 'token_refreshed', { source: 'refresh' });
      getExpoPushToken('refresh', token)
        .then((expoToken) => registerToken(expoToken, sessionUserId, 'refresh'))
        .catch((err) => {
          trackPushEvent('registration', 'failed', {
            source: 'refresh',
            ...pushErrorMetadata(err, 'refresh_register_flow'),
          });
          if (__DEV__) {
            console.warn('[push] Failed to register refreshed push token:', err);
          }
        });
    });

    return () => subscription.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user.id]);
}

export function useUnregisterPushNotifications() {
  const unregisterDevice = trpc.notifications.unregisterDevice.useMutation();

  return async () => {
    if (Platform.OS === 'web') return;

    try {
      trackPushEvent('unregister', 'started');
      const expoToken = await getStoredPushToken();
      if (!expoToken) {
        trackPushEvent('unregister', 'skipped', { reason: 'missing_stored_token' });
        return;
      }

      await unregisterDevice.mutateAsync({ expoToken });
      await clearStoredPushToken();
      trackPushEvent('unregister', 'succeeded');
    } catch (err) {
      trackPushEvent('unregister', 'failed', {
        ...pushErrorMetadata(err, 'unregister_device'),
      });
      if (__DEV__) {
        console.warn('[push] Failed to unregister push notifications:', err);
      }
    }
  };
}
